import { useHomeContextUser } from "../../homeContext";
import { useEffect, useMemo } from "react";
import { UserPreferenceKey } from "common/api/decimal/methods/user";
import useSingleToast from "../../../root/core-ui/toast/useSingleToast";
import FacultySurveyToast from "./FacultySurveyToast";
import { useBrandLoaderData } from "../../../root/rootLoader";
import useDismissibleFeature from "../../actions/user/useDismissibleFeature";
import { isNative } from "common/utils/native";

// Constants moved inline since they're only used in this file
const FACULTY_SURVEY_URL = "https://www.surveymonkey.com/r/UserResearchFaculty";
const SUPPRESSION_MONTHS = 4;
/**
 * Hook that manages the faculty survey toast display logic.
 * Shows a survey toast for faculty users every 4 months unless dismissed.
 * Only appears on allowed brand domains for users with instructor role.
 *
 * @returns void - Side effect hook that shows toast when conditions are met
 */
export default function useFacultySurveyToast() {
  const user = useHomeContextUser();
  const brand = useBrandLoaderData();
  const [showToast] = useSingleToast();

  // Check conditions for showing the survey
  const isAllowedBrand = brand?.InstructorHome?.Survey?.enabled;
  const isFaculty = user.instructor.hasRole;
  const isNativeApp = isNative();

  const { shouldShow, dismiss } = useDismissibleFeature({
    preferenceKey: UserPreferenceKey.FacultySurveyDismissedDate,
    suppressionMonths: SUPPRESSION_MONTHS,
    isFeatureAvailable: isAllowedBrand && isFaculty && !isNativeApp,
  });

  // Build survey URL with parameters
  const href = useMemo(() => {
    const url = new URL(FACULTY_SURVEY_URL);
    const institutions = user.instructor.institutions ?? [];
    const schoolName =
      institutions.length > 0
        ? institutions.map((inst) => inst.name).join(", ")
        : "Unknown";
    url.searchParams.set("SCHOOL_NAME", schoolName);
    url.searchParams.set("USER_ID", user.guid);
    return url.toString();
  }, [user]);

  useEffect(() => {
    if (shouldShow) {
      showToast(<FacultySurveyToast onInteract={dismiss} href={href} />);
    }
  }, [shouldShow, showToast, dismiss, href]);
}
